import React, {createContext, useContext, useRef} from 'react';
import {useParams} from 'react-router-dom';
import useResizeObserver from 'use-resize-observer';

import {
  useDynamicBinSizing,
  useRampFlagFullFidelityRunsPerChart,
} from '../../util/rampFeatureFlags';
import {getDatadogRum} from './../../util/datadog/datadogRum';

type PanelInteractionContextType = {
  numBucketsByPanelWidth: number | undefined;
  setZooming: (isZooming: boolean) => void;
  sizeRef: ReturnType<typeof useResizeObserver>['ref'];
  zooming: boolean;
};

export const PanelInteractionContext =
  createContext<PanelInteractionContextType | null>(null);

// Log how wide the panels are on when a panel loads so we can get a distribution of user panel sizes
export const useTrackPanelWidth = (width: number | undefined) => {
  const DD_RUM = getDatadogRum();
  const hasLogged = useRef(false);

  if (width && !hasLogged.current) {
    hasLogged.current = true;
    DD_RUM.addAction('workspace_component_measure', {runLinePlotWidth: width});
  }
};

export const PanelInteractionProvider = ({
  activeRunLimit,
  children,
}: {
  activeRunLimit: number | undefined;
  children: React.ReactNode;
}) => {
  const [zooming, setZooming] = React.useState(false);
  const {entityName} = useParams<{entityName: string}>();
  /**
   * We don't have any wrapping elements at this height in the React tree, so we're going to produce a ref to send downstream to the layout component. This will let us avoid creating an extra wrapping div.
   */
  const {ref, width} = useResizeObserver();
  useTrackPanelWidth(width);
  const canUseDynamicBinSizing = useDynamicBinSizing(entityName);
  const canOverrideMaxRuns = useRampFlagFullFidelityRunsPerChart();

  const numBucketsByPanelWidth = getPointsPerPanel({
    activeRunLimit,
    canOverrideMaxRuns,
    canUseDynamicBinSizing,
    width,
  });

  const derivedPointsByPanelWidth = numBucketsByPanelWidth
    ? Math.floor(numBucketsByPanelWidth)
    : undefined;

  const value = React.useMemo(
    () => ({
      numBucketsByPanelWidth: derivedPointsByPanelWidth,
      setZooming: (isZooming: boolean) => {
        setZooming(isZooming);
      },
      sizeRef: ref,
      width,
      zooming,
    }),
    [derivedPointsByPanelWidth, setZooming, ref, width, zooming]
  );
  return (
    <PanelInteractionContext.Provider value={value}>
      {children}
    </PanelInteractionContext.Provider>
  );
};

export function getPointsPerPanel({
  activeRunLimit,
  canOverrideMaxRuns,
  canUseDynamicBinSizing,
  width,
}: {
  activeRunLimit: number | undefined;
  canOverrideMaxRuns: boolean;
  canUseDynamicBinSizing: boolean;
  width: number | undefined;
}) {
  // make sure this is first because this needs to return undefined until we have a panel width
  if (width === undefined) {
    return undefined;
  }

  if (!canUseDynamicBinSizing && !canOverrideMaxRuns) {
    return 1000;
  }

  /**
   * We apply a compression factor when we're allowing more than 50 runs per chart with the ramp flag AND when a user has configured the max runs limit in excess of the standard limit for FF. As the user limit exceeds 50 by larger and larger amounts we want to progressively compress the number of points per panel to shave load off of the chart rendering process in React.
   */
  // 100 runs
  // progressiveFactor =(100 runs - 50)/ 50 => 1
  // factor = 1 - (.5 * 1) => 0.5
  //
  // 75 runs
  // progressiveFactor =(75 runs - 50)/ 50 => 0.5
  // factor = 1 - (.5 * 0.5) => 0.75
  //
  // 50 runs
  // progressiveFactor =(50 runs - 50)/ 50 => 0
  // factor = 1 - (.5 * 0) => 1
  const applyCompressionFactor =
    activeRunLimit && activeRunLimit > 50 && canOverrideMaxRuns;
  // don't let this number exceed 1 or fall below 0
  const progressiveFactor = Math.max(
    0,

    Math.min(1, ((activeRunLimit ?? 50) - 50) / 50)
  );

  const factor = !applyCompressionFactor ? 1 : 1 - 0.5 * progressiveFactor;

  if (width === undefined) {
    return undefined;
  }

  if (width > 500) {
    return 1000 * factor;
  }

  if (width > 400) {
    return 800 * factor;
  }

  if (width > 300) {
    return 600 * factor;
  }

  if (width > 200) {
    return 500 * factor;
  }

  return 400 * factor;
}

export function usePanelInteractionContext() {
  const context = useContext(PanelInteractionContext);

  if (!context) {
    console.error(
      'usePanelInteractionContext must be used within a PanelInteractionProvider'
    );

    return {
      numBucketsByPanelWidth: 1000,
      setZooming: () => {},
      sizeRef: null,
      zooming: false,
    };
  }

  return context;
}
